 <template>
	<prosemirror-adapter-provider>
		<anchor-element v-if="visible" :chain="chain" tag="div" class="html-area likeInput"
						:class="[clazz, errorClass, ro ? 'read-only':null, toolbarVisible ? 'like-focus':null]"
						:style="[style, extraStyles]" v-bind="attrs"
						ref="anchor"></anchor-element>
		<boat-element :chain="chain" :tag="ToolBar" :toolbar="toolbar" @action="toolbarAction"></boat-element>
		<ui-root v-if="isDialog" :part="dialogForm" :data="dialogData" :readonly="false" :viewmode="false"
				   :display="<ModalDialogDisplay>{ mode: 'modal', moveable: true, resizable: true }"
				   :actions="dialogControls(env, ['cancel', 'ok'])"
				   @close="dialogClosed"></ui-root>
	</prosemirror-adapter-provider>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "../Editor";
import {computed, nextTick, onMounted, ref, watch, onUnmounted, watchEffect, type CSSProperties} from "vue";
import type {HtmlArea} from "@t/fd/editor/HtmlArea.ts";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree.ts";
import ToolBar from "@/ui/toolbar/ToolBar.vue";
import {BlockTypeChange, buildToolbar, findComponent, Toolbar, ToolbarMenu, ToolbarMenuItem, ToolbarMenuItems} from "@t/ui/Toolbar.ts";
import {injectEnv, provideEventBus, provideNewEventBus} from "@fd/Environment.ts";
import {ProsemirrorAdapterProvider} from "@prosemirror-adapter/vue";
import {fromString} from "@fd/editors/html/Utils.ts";
import {EditorState, type Command, Selection} from "prosemirror-state";
import {DOMParser, DOMSerializer} from "prosemirror-model";
import {EditorView} from "prosemirror-view";
import {editorSupport, UiCommand} from "@fd/editors/html/EditorSupport.ts";
import type {FormData} from "@t/fd/FormPart.ts";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {ModalDialogDisplay} from "@t/ui/Dialog.ts";
import {useTemplateStore} from "@/store/TemplateStore.ts";
import {SelectHierarchy, SelectHierarchyItem} from "@t/ui/SelectHierarchy.ts";
import {TemplateVariables} from "@t/template/Templates.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const area = computed<HtmlArea>(() => props.part as HtmlArea);
const {visible, value, clazz, errorClass, style, ro, vm, tabindexBinding} = useEditor(props);

const env = injectEnv();
const bus = provideNewEventBus();

const ts = useTemplateStore();
const anchor = ref<InstanceType<typeof AnchorElement>>(null)
const chain = createChain({
	managed: true,	/* Cannot be autoclosed */
	rule: {
		ah: 0, av: 0,
		bh: 0, bv: 100,
		x: 0, y: -4
	}
}, "html-toolbar");

const editorElement = computed<HTMLElement|null>(() => anchor.value ? anchor.value.element() : null);
let resizeObserver:ResizeObserver = null;
const areaWidth = ref<number>(area.value.width ?? -1);
const areaHeight = ref<number>(area.value.height ?? -1);

const inFocus = ref<boolean>(false);
const isDialog = ref<boolean>(false);
const dialogData = ref<FormData>({});
const dialogForm = ref<InterfaceRoot<FormDefinition>|null>(null);

let wasEdit = false;

const supp = editorSupport(env, (state) => {
	chain.closeChildren();	// close drop menu if any open
});

const extraStyles = computed<CSSProperties>(() => {
	const s:CSSProperties = {};
	if (area.value.resize) s.resize = area.value.resize;
	if (areaWidth.value > 0) s.width = areaWidth.value + "px";
	if (areaHeight.value > 0) s.height = areaHeight.value + "px";
	return s;
});
function selectItemToMenuItem(shi:SelectHierarchyItem):ToolbarMenuItem {
	const tmi:ToolbarMenuItem = {
		type: "menuitem",
		id: "template-"+shi.id,
		title: shi.text
	};
	if (shi.submenu && Array.isArray(shi.submenu.items)) {
		tmi.children = [];
		for (let child of shi.submenu.items) {
			tmi.children.push(selectItemToMenuItem(child));
		}
	}
	return tmi;
}

async function templateMenuBuilder():Promise<ToolbarMenuItems> {
	const temps:SelectHierarchy = await ts.loadApplicableTemplates(area.value.templatesFor, area.value.templatesForSpec);
	// Convert SelectHierarchy to menu items
	const items:ToolbarMenuItems = [];
	for (let shi of temps.items) {
		items.push(selectItemToMenuItem(shi));
	}
	return items;
}

const toolbar = computed<Toolbar>(() => {
	const { blockType, selectionEmpty, selectionWithLinks } = supp;
	const bar = buildToolbar(env.msg, (tc) => {
		if (tc.id.startsWith("justify")) {
			return blockType.value === "p" || /h\d/g.test(blockType.value);
		} else if (["p", "h1", "h2", "h3", "h4", "h5", "h6", "pre"].includes(tc.id)) {
			return blockType.value !== "dt" && blockType.value !== "dd";
		}
		switch (tc.id) {
			case "dt":
			case "dd":
				return blockType.value === "dt" || blockType.value === "dd";
			case "link":
				return !selectionEmpty.value && !selectionWithLinks.value;
			case "unlink":
				return selectionWithLinks.value;
			case "olist":
				return supp.canOrderedList.value;
			case "ulist":
				return supp.canBulletList.value;
			case "dlist":
				return supp.canDescList.value;
			case "indent":
				return supp.canIndent.value;
			case "outdent":
				return supp.canOutdent.value;
			case "blockquote":
				return supp.canBlockquote.value;
			case "insertTable":
				return !supp.canDeleteTable.value;
			case "tableMenu":
			case "deleteTable":
			case "addTable":
			case "insertColumnBefore":
			case "insertColumnAfter":
			case "insertRowBefore":
			case "insertRowAfter":
			case "deleteColumn":
			case "deleteRow":
			case "toggleHeaderRow":
			case "toggleHeaderColumn":
			case "toggleHeaderCell":
				return supp.canDeleteTable.value;
			case "mergeCells":
				return supp.canMergeCells.value;
			case "splitCell":
				return supp.canSplitCell.value;
			//case "insertImage":
		}
		if (tc.id === "applyTemplate") {
			if (area.value.templatesFor) (tc as ToolbarMenu).items = templateMenuBuilder;
			else return false;	// not enabled
		}
		return true;
	});
	const styleMenu = findComponent(bar, "block_style_menu") as ToolbarMenu;
	if (styleMenu) {
		const styleItem = findComponent(bar, blockType.value) as ToolbarMenuItem;
		if (styleItem) styleMenu.title = styleItem.title;
	}
	return bar;
});

const toolbarVisible = computed<boolean>(() => (inFocus.value || chain.interacting.value) && !isDialog.value);
watch(toolbarVisible, (vis) => {
	if (vis) chain.show();
	else chain.hide();
})

const attrs = computed(() => {
	const a: any = tabindexBinding();
	//if (!ro.value && !vm.value) a.contenteditable = "";
	return a;
});


let view:EditorView = null;

function saveContent(state:EditorState) {
	const serializer = DOMSerializer.fromSchema(state.schema);
	const fragment = serializer.serializeFragment(state.doc.content);
	const div = document.createElement("div");
	div.appendChild(fragment);
	//console.log("HTML = " + div.innerHTML);
	value.value = div.innerHTML;
	wasEdit = false;
}


watchEffect((onCleanup) => {
	const el = editorElement.value;
	if (el) {
		startObservingSize(el);
	} else {
		stopObservingSize();
		return;
	}

	const state:EditorState = EditorState.create({
		doc: DOMParser.fromSchema(supp.schema).parse(fromString(value.value)),
		schema: supp.schema,
		plugins: [ ...supp.plugins ]
	});

	view = new EditorView(el, {
		state,
		dispatchTransaction(tr) {
			wasEdit = wasEdit || tr.docChanged;
			let newState = view.state.apply(tr);
			view.updateState(newState);
		},
		handleDOMEvents: {
			focus: ()=> {
				//console.log("---focus");
				inFocus.value = true;
			},

			blur: (view:EditorView) => {
				inFocus.value = false;
				//console.log("---blur");
				if (wasEdit) saveContent(view.state);
			}
		}
	});
	// We have our own table editing
	document.execCommand('enableObjectResizing', false, 'false');
	document.execCommand('enableInlineTableEditing', false, 'false');

	onCleanup(() => {
		view.destroy();
		view = null;
	});
});

function applyCommand(command:Command):boolean {
	if (view) {
		return command(view.state, view.dispatch, view);
	}
	return false;
}

function applyBlockTypeChange(bt:BlockTypeChange) {
	return applyCommand(supp.modifyBlockType(bt.node, bt.attrs));
}

let lastUiCommand:UiCommand<any> = null;

function applyUiCommand<D>(command:UiCommand<D>):boolean {
	if (view) {
		savedSelection = view.state.selection.toJSON();
		const data: D = command.extract(view.state);
		if (data) {
			dialogData.value = data;
			dialogForm.value = command.form;
			isDialog.value = true;	// show the dialog
			lastUiCommand = command;
			return true;
		}
	}
	return false;
}

let savedSelection:any = null;

function dialogClosed(selectionRestore = true) {
	// Clean Up and Close
	dialogData.value = null;
	dialogForm.value = null;
	isDialog.value = false;
	lastUiCommand = null;
	nextTick(() => {
		if (view) {
			view.focus();
			if (selectionRestore && savedSelection) {
				const cmd:Command = (state:EditorState, dispatch?):boolean => {
					const tr = state.tr;
					tr.setSelection(Selection.fromJSON(tr.doc, savedSelection));
					if (dispatch) dispatch(tr);
					return true;
				}
			}
		}
	});
}

const unregCommands = bus.onAnyCommand(async (cmd:string) => {
	if (cmd === "ok" && lastUiCommand && view) {
		const cmd:Command = lastUiCommand.prepare(dialogData.value, savedSelection);
		const ok = cmd(view.state, view.dispatch, view);
		dialogClosed(false /* Restored by action command */);
		return ok ? "ok" : "fail";
	}
	dialogClosed(false /* Restored by action command */);
	return "pass";
});

function applyTemplate(templateId: number):boolean {
	if (view) {
		const selected = supp.getSelectedHTML(view.state);
		const vars:TemplateVariables = {
			html: selected
		};
		ts.applyTemplate(templateId, vars)
			.then((templateText:string) => {
				applyCommand(supp.insertHTML(templateText));
			});
		return true;
	}
	return false;
}

function toolbarAction(id: string, arg?: any):boolean {
	if (id.startsWith("template-")) return applyTemplate(parseInt(id.substring(9)))
	switch (id) {
		case "bold":
			return applyCommand(supp.bold);
		case "italic":
			return applyCommand(supp.italic);
		case "underline":
			return applyCommand(supp.underline);
		case "strike":
			return applyCommand(supp.strike);
		case "subscript":
			return applyCommand(supp.subscript);
		case "superscript":
			return applyCommand(supp.superscript);
		case "justifyLeft":
			return applyCommand(supp.alignLeft);
		case "justifyCenter":
			return applyCommand(supp.alignCenter);
		case "justifyRight":
			return applyCommand(supp.alignRight);
		case "justifyFull":
			return applyCommand(supp.alignJustify);
		case "removeFormat":
			return applyCommand(supp.play);
		case "unlink":
			return applyCommand(supp.unlink);
		case "link":
			return applyUiCommand(supp.link);
		case "mark":
			return applyCommand(supp.mark);
		case "p":
		case "pre":
		case "h1":
		case "h2":
		case "h3":
		case "h4":
		case "h5":
		case "h6":
		case "dt":
		case "dd":
			return applyBlockTypeChange(arg as BlockTypeChange);
		case "olist":
			return applyCommand(supp.orderedList);
		case "ulist":
			return applyCommand(supp.bulletList);
		case "dlist":
			return applyCommand(supp.descList);
		case "outdent":
			return applyCommand(supp.outdent);
		case "indent":
			return applyCommand(supp.indent);
		case "blockquote":
			return applyCommand(supp.blockquote);
		case "color":
			return applyCommand(supp.setColor(arg));
		case "hr":
			return applyCommand(supp.insertHorizontalRule);
		case "insertTable":
		case "addTable":
			return applyCommand(supp.insertTable);
		case "deleteTable":
			return applyCommand(supp.deleteTable);
		case "insertColumnBefore":
			return applyCommand(supp.addColumnBefore);
		case "insertColumnAfter":
			return applyCommand(supp.addColumnAfter);
		case "insertRowBefore":
			return applyCommand(supp.addRowBefore);
		case "insertRowAfter":
			return applyCommand(supp.addRowAfter);
		case "deleteColumn":
			return applyCommand(supp.deleteColumn);
		case "deleteRow":
			return applyCommand(supp.deleteRow);
		case "mergeCells":
			return applyCommand(supp.mergeCells);
		case "splitCell":
			return applyCommand(supp.splitCell);
		case "toggleHeaderRow":
			return applyCommand(supp.toggleHeaderRow);
		case "toggleHeaderColumn":
			return applyCommand(supp.toggleHeaderColumn);
		case "toggleHeaderCell":
			return applyCommand(supp.toggleHeaderCell);
		case "insertImage":
			return applyUiCommand(supp.insertImage);
		default:
			console.log("Action '" + id + "' not yet supported (arg = " + arg + ")");
	}
}


function resizeCallback(entries:ResizeObserverEntry[]) {
	if (entries.length > 0) {
		const editorWrapper = entries[0].target as HTMLElement;
		const m = getElementMeasure(editorWrapper);
		if (area.value.resize === "both" || area.value.resize === "vertical") {
			areaHeight.value = m.height;
		}
		if (area.value.resize === "both" || area.value.resize === "horizontal") {
			areaWidth.value = m.width;
		}
	}
}

function startObservingSize(el:HTMLElement) {
	if (resizeObserver == null) {
		resizeObserver = new ResizeObserver(resizeCallback);
		resizeObserver.observe(el);
	}
}

function stopObservingSize() {
	if (resizeObserver != null) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
}

onUnmounted(() => {
	stopObservingSize();
	unregCommands();
});
</script>

<style>
.html-area.likeInput {
	line-height: var(--input-height);
	min-height: var(--input-height);
	max-height: none;
	width: 100%;
	height: auto;
	overflow: auto;

	/* Paragraphs inside list items */
	& li > p {
		margin-block: 0;
	}

	/*& p, & li, & dd, & dt,
	& ol, & ul, & dl {
		margin-block: 0;
		line-height: var(--input-height);
	}*/

	& code {
		color: color-mix(in srgb, var(--fg) 66%, red);
	}

	& pre {
		background-color: #6699FF22;
		padding: 4px 8px;
		border-radius: 4px;

		& > code {
			background-color: transparent;
			color: var(--fg);
		}
	}

	& kbd {
		outline: 1px solid rgb(from var(--fg) r g b / 0.2);
		padding-inline: 2px;
		border-radius: 3px;
		background-color: rgb(from var(--fg) r g b / 0.03);
	}

	& var {
		color: color-mix(in srgb, var(--fg) 50%, lightseagreen);
		font-style: normal;
		font-family: var(--font-mono);
	}

	& dt {
		font-weight: bold;
	}

	& table {
		--default-cell-min-width: 20px;
		border-collapse: collapse;
		table-layout: fixed;
		overflow: hidden;
		border-left: 1px solid rgb(from var(--fg) r g b / 0.2);
		border-top: 1px solid rgb(from var(--fg) r g b / 0.2);
		margin-bottom: 8px;

		& > tbody {
			& > tr {
				& > td, & > th {
					padding: 4px 8px;
					min-width: 20px;
					text-align: left;
					vertical-align: top;
					box-sizing: border-box;
					position:relative;
					border-right: 1px solid rgb(from var(--fg) r g b / 0.2);
					border-bottom: 1px solid rgb(from var(--fg) r g b / 0.2);

					&:not([data-colwidth]):not(.column-resize-dragging) {
						/* if there's no explicit width set and the column is not being resized, set a default width */
						min-width: var(--default-cell-min-width);
					}

					& > p {
						margin-block: 0;
					}
				}
				& > th {
					background-color: rgb(from var(--fg) r g b / 0.05);
				}
			}
		}
	}

	& > .ProseMirror {
		/* Inner prose mirror element takes all available height of the wrapper */
		min-height: 100%;
	}
}

.html-debug {
	font-size: 10px;
	line-height: 12px;
	font-family: var(--font-mono);
	max-width: 600px;
	padding-inline: 8px;
	word-break: break-all;
}

.ProseMirror {
	&:focus, &:focus-visible {
		outline:none;
	}

	& .tableWrapper {
		overflow-x: auto;
	}

	& .column-resize-handle {
		position: absolute;
		right: -2px;
		top: 0;
		bottom: 0;
		width: 4px;
		z-index: 20;
		background-color: var(--selection-bg);
		pointer-events: none;
	}

	&.resize-cursor {
		cursor: col-resize;
	}

	/* Give selected cells overlay */
	& .selectedCell:after {
		z-index: 2;
		position: absolute;
		content: '';
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		background: rgb(from var(--selection-bg) r g b / 0.2);
		pointer-events: none;
	}

	/* Eliminate bottom margin of the last paragraph of the editor (PM don't focus when clicking on the margin) */
	& > p:last-child {
		margin-block-end: 0;
		padding-bottom: 1.5rem;	/* This is not necessary but it is here to give the user a visual clue that this is the end of the document. */
	}
}

.shadow-selection {
	background-color: color-mix(in srgb, var(--selection-bg) 50%, var(--bg));
}
</style>